import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useCompany } from '../../hooks/useCompany';
import { hasPermission } from '../../utils/permissions';
import type { TeamRole } from '../../types/company';

interface ProtectedCompanyRouteProps {
  children: ReactNode;
  requiredRole?: TeamRole;  // Minimum role required (default: VIEWER)
  redirectTo?: string;      // Where to redirect if not authenticated
}

/**
 * Route guard for company pages.
 *
 * Checks:
 * 1. User is authenticated (redirects to signin if not)
 * 2. User belongs to a company (shows error if not member)
 * 3. User has required role (hides content if insufficient)
 *
 * Per CONTEXT.md:
 * - Non-member access: Show "You need to be invited by the company"
 * - Insufficient role: Hide controls proactively
 * - Loading: Show skeleton/shimmer placeholders
 */
export default function ProtectedCompanyRoute({
  children,
  requiredRole = 'VIEWER',
  redirectTo = '/signin',
}: ProtectedCompanyRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { company, myRole, isLoading: companyLoading, error } = useCompany();
  const location = useLocation();
  const { t, i18n } = useTranslation('company');

  const isLoading = authLoading || companyLoading;

  // Loading state - show skeleton per CONTEXT.md
  if (isLoading) {
    return (
      <div
        className="min-h-screen bg-white flex items-center justify-center"
        dir={i18n.dir()}
      >
        <div className="text-center space-y-4">
          {/* Shimmer placeholder */}
          <div className="animate-pulse">
            <div className="h-8 w-48 bg-gray-200 rounded mx-auto mb-4" />
            <div className="h-4 w-32 bg-gray-200 rounded mx-auto" />
          </div>
          <p className="text-gray-500 text-sm">{t('loadingCompany', 'Loading company...')}</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to signin
  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Not a company member - show error per CONTEXT.md
  // "Show error message 'You need to be invited by the company' - not a silent 404"
  if (!company) {
    return (
      <div
        className="min-h-screen bg-white flex items-center justify-center p-4"
        dir={i18n.dir()}
      >
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-building-line text-2xl text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] mb-2">
            {t('noCompany', 'No Company Access')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('notMemberError', 'You need to be invited by the company to access this page.')}
          </p>
          <a
            href="/"
            className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('common:backToHome', 'Back to Home')}
          </a>
        </div>
      </div>
    );
  }

  // Check role permission - hide content if insufficient
  // Per CONTEXT.md: "Hide controls proactively (VIEWER doesn't see edit buttons)"
  if (!hasPermission(myRole, requiredRole)) {
    return (
      <div
        className="min-h-screen bg-white flex items-center justify-center p-4"
        dir={i18n.dir()}
      >
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-lock-line text-2xl text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] mb-2">
            {t('insufficientPermission', 'Insufficient Permissions')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('roleRequired', 'You do not have permission to access this page.')}
          </p>
          <a
            href="/company/dashboard"
            className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('backToDashboard', 'Back to Dashboard')}
          </a>
        </div>
      </div>
    );
  }

  // Error state (network error, not 403)
  if (error) {
    return (
      <div
        className="min-h-screen bg-white flex items-center justify-center p-4"
        dir={i18n.dir()}
      >
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-[var(--pv-color-primary)] mb-2">
            {t('common:error', 'Error')}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('common:retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  // All checks passed - render children
  return <>{children}</>;
}
